import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  getFacturadorEmpresas,
  responderIntercambioFacturador,
  getReceptoresFacturador,
  vistaPreviaSetFacturador,
  enviarSetFacturador,
  getEnviosCertificacion,
  consultarEstadoSetFacturador,
  reenviarSetFacturador,
  descargarMuestrasSet,
  enviarLibroFacturador,
  getLibrosCertificacion,
  consultarEstadoLibroFacturador,
  vistaPreviaSimulacionFacturador,
  enviarSimulacionFacturador,
} from '../services/adminApi';
import { NOMBRES_TIPO, pesos, fechaCorta, mensajeError, EstadoBadge } from './Facturador/comun';
import '../styles/pages/Facturador.css';
import '../styles/pages/AdminFacturadorPage.css';

/** Descarga un archivo que llega en base64: las respuestas van en ISO-8859-1 y no se deben recodificar. */
function descargar({ nombre, base64 }) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  guardarArchivo(new Blob([bytes], { type: 'application/xml' }), nombre);
}

function guardarArchivo(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(url);
}

/**
 * Bloquea la pantalla mientras corre un proceso: un aviso con indicador de carga
 * tapa la página (no se puede presionar nada debajo ni cerrarlo) y recargar o
 * cerrar la pestaña pide confirmación. Salir no corta el proceso en el
 * servidor; el bloqueo evita lanzar otro encima y perder el resultado.
 * @template T
 * @param {string} titulo
 * @param {() => Promise<T>} proceso
 * @returns {Promise<T>}
 */
async function conBloqueo(titulo, proceso) {
  const avisarAlSalir = (evento) => {
    evento.preventDefault();
    evento.returnValue = '';
  };
  window.addEventListener('beforeunload', avisarAlSalir);
  Swal.fire({
    title: titulo,
    text: 'No cierres ni recargues la página hasta que termine.',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });
  try {
    return await proceso();
  } finally {
    window.removeEventListener('beforeunload', avisarAlSalir);
    Swal.close();
  }
}

const RECEPTOR_VACIO = { rut: '', razonSocial: '', giro: '', direccion: '', comuna: '' };
const CAMPOS_RECEPTOR = [
  ['rut', 'RUT'],
  ['razonSocial', 'Razón social'],
  ['giro', 'Giro'],
  ['direccion', 'Dirección'],
  ['comuna', 'Comuna'],
];
const NOMBRES_SET = { basico: 'Set básico', factura_compra: 'Set de factura de compra', simulacion: 'Simulación' };
const NOMBRES_LIBRO = { libro_ventas: 'Libro de ventas', libro_compras: 'Libro de compras' };
/** Documentos en papel que trae el libro de compras del set; los electrónicos usan NOMBRES_TIPO. */
const NOMBRES_PAPEL = { 30: 'Factura', 45: 'Factura de compra', 55: 'Nota de débito', 60: 'Nota de crédito' };

const nombreCompra = (tipo) =>
  NOMBRES_PAPEL[tipo] ? `${NOMBRES_PAPEL[tipo]} (papel)` : `${NOMBRES_TIPO[tipo] || `Documento ${tipo}`} electrónica`;

/** El IVA que no queda como recuperable: de uso común, no recuperable o retenido. */
function otroRegistro(d) {
  if (d.IVAUsoComun) return `IVA de uso común ${pesos(d.IVAUsoComun)}`;
  if (d.IVANoRec) return `IVA no recuperable (código ${d.IVANoRec.CodIVANoRec}) ${pesos(d.IVANoRec.MntIVANoRec)}`;
  if (d.OtrosImp) return `IVA retenido total (código ${d.OtrosImp.CodImp}) ${pesos(d.OtrosImp.MntImp)}`;
  return '—';
}

// ─── Receptor de un documento del set ─────────────────────────────────────────

function ReceptorDelSet({ etiqueta, valor, guardados, onCambio }) {
  const elegir = (rut) => {
    const r = guardados.find((g) => g.rut === rut);
    if (r) onCambio({ rut: r.rut, razonSocial: r.razonSocial, giro: r.giro ?? '', direccion: r.direccion ?? '', comuna: r.comuna ?? '' });
  };
  return (
    <div className="fe-cert-receptor">
      <strong className="fe-cert-receptor-titulo">{etiqueta}</strong>
      {guardados.length > 0 && (
        <div className="fe-form-group">
          <select value="" onChange={(e) => elegir(e.target.value)}>
            <option value="">Elegir un receptor guardado…</option>
            {guardados.map((g) => <option key={g.rut} value={g.rut}>{g.rut} · {g.razonSocial}</option>)}
          </select>
        </div>
      )}
      <div className="fe-form-grid">
        {CAMPOS_RECEPTOR.map(([campo, nombre]) => (
          <div className="fe-form-group" key={campo}>
            <label>{nombre}</label>
            <input value={valor[campo] ?? ''} onChange={(e) => onCambio({ ...valor, [campo]: e.target.value })} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Un set de documentos: receptores y envío ────────────────────────────────

function SetDeDocumentos({ empresa, texto, set, guardados, onEnviado }) {
  const originales = set.casos.filter((c) => !c.referencia);
  const [receptores, setReceptores] = useState(() => originales.map(() => ({ ...RECEPTOR_VACIO })));
  const [giroCorregido, setGiroCorregido] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cambiarReceptor = (i, valor) => setReceptores((actuales) => actuales.map((r, j) => (j === i ? valor : r)));

  const enviar = async () => {
    const confirmacion = await Swal.fire({
      icon: 'question',
      title: `Enviar el ${NOMBRES_SET[set.clase].toLowerCase()}`,
      text: `Se arman y firman ${set.casos.length} documentos con folios de certificación y se envían juntos al SII.`,
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmacion.isConfirmed) return;
    setEnviando(true);
    try {
      const resultado = await conBloqueo(`Enviando el ${NOMBRES_SET[set.clase].toLowerCase()} al SII...`, () =>
        enviarSetFacturador(empresa.id_company, { texto, clase: set.clase, receptores, giroCorregido })
      );
      Swal.fire({
        icon: resultado.trackId ? 'success' : 'warning',
        title: resultado.trackId ? 'Set enviado' : 'El SII no recibió el envío',
        text: resultado.trackId ? `TrackID ${resultado.trackId}. Consulta su estado en unos minutos.` : 'Quedó por enviar: reintenta desde la lista de envíos.',
      });
      onEnviado();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se envió', text: mensajeError(err, 'No se pudo enviar el set') });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="fe-panel">
      <div className="fe-cert-cabecera">
        <h3 className="fe-panel-title">{NOMBRES_SET[set.clase]}</h3>
        <span className="fe-badge fe-badge--gris">N° de atención {set.numeroAtencion}</span>
      </div>
      <ul className="fe-cert-casos">
        {set.casos.map((c) => (
          <li key={c.numero}>Caso {c.numero}: {c.documento}{c.razon ? ` — ${c.razon.toLowerCase()}` : ''}</li>
        ))}
      </ul>
      <div className="fe-cert-receptores">
        {originales.map((caso, i) => (
          <ReceptorDelSet
            key={caso.numero}
            etiqueta={`${caso.documento} del caso ${caso.numero}${caso.tipo === 46 ? ' (vendedor)' : ''}`}
            valor={receptores[i]}
            guardados={guardados}
            onCambio={(valor) => cambiarReceptor(i, valor)}
          />
        ))}
      </div>
      {set.corrigeGiro && (
        <div className="fe-form-group">
          <label>Giro correcto para la nota que corrige el giro (debe decir)</label>
          <input value={giroCorregido} onChange={(e) => setGiroCorregido(e.target.value)} maxLength={80} />
        </div>
      )}
      <div className="fe-form-actions fe-cert-acciones">
        <button className="fe-btn fe-btn--primary" disabled={enviando} onClick={enviar}>
          {enviando ? 'Enviando...' : `Generar y enviar al SII (${set.casos.length} documentos)`}
        </button>
      </div>
    </section>
  );
}

// ─── Un libro del set: vista previa y envío ──────────────────────────────────

function LibroDelSet({ empresa, texto, set, onEnviado }) {
  const [proveedor, setProveedor] = useState({ rut: '', razonSocial: '' });
  const [enviando, setEnviando] = useState(false);
  const { base, detalle, resumen, error } = set.libro;
  const esVentas = set.clase === 'libro_ventas';
  const conUsoComun = (resumen ?? []).find((r) => r.FctProp);

  const enviar = async () => {
    const confirmacion = await Swal.fire({
      icon: 'question',
      title: `Enviar el ${NOMBRES_LIBRO[set.clase].toLowerCase()}`,
      text: `Se arma y firma el libro del período ${base.periodo} y se envía al SII.`,
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmacion.isConfirmed) return;
    setEnviando(true);
    try {
      const libro = await conBloqueo(`Enviando el ${NOMBRES_LIBRO[set.clase].toLowerCase()} al SII...`, () =>
        enviarLibroFacturador(empresa.id_company, { texto, clase: set.clase, ...(esVentas ? {} : { proveedor }) })
      );
      Swal.fire({
        icon: libro.trackId ? 'success' : 'warning',
        title: libro.trackId ? 'Libro enviado' : 'El SII no recibió el libro',
        text: libro.trackId ? `TrackID ${libro.trackId}. Consulta su estado en unos minutos.` : `${libro.glosa ?? ''} Vuelve a enviarlo.`,
      });
      onEnviado();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se envió', text: mensajeError(err, 'No se pudo enviar el libro') });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="fe-panel">
      <div className="fe-cert-cabecera">
        <h3 className="fe-panel-title">{NOMBRES_LIBRO[set.clase]}</h3>
        <span className="fe-badge fe-badge--gris">N° de atención {set.numeroAtencion}</span>
      </div>
      {base ? (
        <p className="fe-hint" style={{ margin: '0 0 12px' }}>
          {esVentas
            ? `Se arma con los ${base.documentos} documentos del set básico aceptado (TrackID ${base.trackId}), período ${base.periodo}.`
            : `Período ${base.periodo}, el mismo del libro de ventas. Todas las compras van con el mismo proveedor.`}
        </p>
      ) : (
        <div className="fe-aviso fe-aviso--amarillo">
          Los libros van sobre el set básico: envíalo y consulta su estado hasta que el SII lo acepte completo.
        </div>
      )}
      {error && <div className="fe-aviso fe-aviso--rojo">{error}</div>}
      {detalle && (
        <>
          <div className="fe-table-container">
          <table className="fe-table">
            <thead>
              <tr>
                <th>Documento</th>
                <th className="fe-num">Exento</th>
                <th className="fe-num">Neto</th>
                <th className="fe-num">IVA</th>
                <th>Otro registro</th>
                <th className="fe-num">Total</th>
              </tr>
            </thead>
            <tbody>
              {detalle.map((d) => (
                <tr key={`${d.TpoDoc}-${d.NroDoc}`}>
                  <td>{nombreCompra(d.TpoDoc)} N° {d.NroDoc}</td>
                  <td className="fe-num">{pesos(d.MntExe)}</td>
                  <td className="fe-num">{pesos(d.MntNeto)}</td>
                  <td className="fe-num">{pesos(d.MntIVA)}</td>
                  <td>{otroRegistro(d)}</td>
                  <td className="fe-num">{pesos(d.MntTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {conUsoComun && (
            <p className="fe-hint" style={{ margin: '8px 0 12px' }}>
              Factor de proporcionalidad {String(conUsoComun.FctProp).replace('.', ',')}: crédito por IVA de uso común{' '}
              {pesos(conUsoComun.TotCredIVAUsoComun)}.
            </p>
          )}
          <div className="fe-cert-receptor" style={{ maxWidth: 560 }}>
            <strong className="fe-cert-receptor-titulo">Proveedor de las compras (RUT válido)</strong>
            <div className="fe-form-grid">
              <div className="fe-form-group">
                <label>RUT</label>
                <input value={proveedor.rut} onChange={(e) => setProveedor({ ...proveedor, rut: e.target.value })} />
              </div>
              <div className="fe-form-group">
                <label>Razón social</label>
                <input value={proveedor.razonSocial} onChange={(e) => setProveedor({ ...proveedor, razonSocial: e.target.value })} maxLength={50} />
              </div>
            </div>
          </div>
        </>
      )}
      <div className="fe-form-actions fe-cert-acciones">
        <button className="fe-btn fe-btn--primary" disabled={enviando || !base || Boolean(error)} onClick={enviar}>
          {enviando ? 'Enviando...' : 'Generar y enviar al SII'}
        </button>
      </div>
    </section>
  );
}

// ─── Envíos de certificación ─────────────────────────────────────────────────

function EnviosCertificacion({ empresa, envios, onCambio }) {
  const [trabajando, setTrabajando] = useState(null);

  const ejecutar = async (lote, accion) => {
    setTrabajando(lote.id);
    try {
      if (accion === 'muestras') {
        const nombre = lote.set === 'simulacion' ? 'muestras-simulacion.zip' : `muestras-set-${lote.numeroAtencion}.zip`;
        guardarArchivo(await conBloqueo('Generando las muestras impresas...', () => descargarMuestrasSet(empresa.id_company, lote.id)), nombre);
      } else {
        const r = accion === 'estado'
          ? await conBloqueo('Consultando el estado en el SII...', () => consultarEstadoSetFacturador(empresa.id_company, lote.id))
          : await conBloqueo('Reenviando al SII...', () => reenviarSetFacturador(empresa.id_company, lote.id));
        if (r.respuesta) {
          const { estado, glosa, informados, aceptados, rechazados, reparos } = r.respuesta;
          Swal.fire({
            icon: rechazados > 0 || reparos > 0 ? 'warning' : 'info',
            title: `Estado del envío: ${estado}`,
            html: `${glosa ?? ''}<br/>Informados ${informados} · aceptados ${aceptados} · rechazados ${rechazados} · con reparos ${reparos}`,
          });
        }
        onCambio();
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo completar la acción') });
    } finally {
      setTrabajando(null);
    }
  };

  if (envios.length === 0) return <p className="fe-hint">Todavía no hay envíos de certificación.</p>;
  return envios.map((lote) => {
    const porEnviar = lote.documentos.every((d) => d.estado === 'por_enviar');
    const estados = [...new Set(lote.documentos.map((d) => d.estado))];
    return (
      <section key={lote.id} className="fe-panel">
        <div className="fe-cert-cabecera">
          <h3 className="fe-panel-title">
            {NOMBRES_SET[lote.set] ?? lote.set}{lote.numeroAtencion ? ` · N° de atención ${lote.numeroAtencion}` : ''}
          </h3>
          <span className="fe-cert-track">
            {estados.length === 1 && <EstadoBadge estado={estados[0]} />}
            <span className="fe-muted">{lote.trackId ? `TrackID ${lote.trackId}` : 'Sin TrackID'}</span>
          </span>
        </div>
        <div className="fe-table-container">
        <table className="fe-table">
          <thead>
            <tr><th>{lote.set === 'simulacion' ? 'Original' : 'Caso'}</th><th>Documento</th><th>Receptor</th><th className="fe-num">Total</th><th>Estado</th></tr>
          </thead>
          <tbody>
            {lote.documentos.map((d) => (
              <tr key={d.id}>
                <td>{d.caso}</td>
                <td>{NOMBRES_TIPO[d.tipo] || `Documento ${d.tipo}`} N° {d.folio}</td>
                <td>{d.receptor}</td>
                <td className="fe-num">{pesos(d.total)}</td>
                <td><EstadoBadge estado={d.estado} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="fe-form-actions fe-cert-acciones">
          {porEnviar ? (
            <button className="fe-btn fe-btn--primary" disabled={trabajando === lote.id} onClick={() => ejecutar(lote, 'reenviar')}>Reenviar al SII</button>
          ) : (
            <button className="fe-btn fe-btn--secondary" disabled={trabajando === lote.id} onClick={() => ejecutar(lote, 'estado')}>Consultar estado en el SII</button>
          )}
          <button className="fe-btn fe-btn--success" disabled={trabajando === lote.id} onClick={() => ejecutar(lote, 'muestras')}>Descargar muestras impresas (ZIP)</button>
        </div>
      </section>
    );
  });
}

// ─── Simulación ──────────────────────────────────────────────────────────────

function Simulacion({ empresa, onEnviado }) {
  const [archivo, setArchivo] = useState(null);
  const [vista, setVista] = useState(null);
  const [revisando, setRevisando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const revisar = async (elegido) => {
    setRevisando(true);
    try {
      setVista(await conBloqueo('Revisando los documentos...', () => vistaPreviaSimulacionFacturador(empresa.id_company, elegido)));
    } catch (err) {
      setVista(null);
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo leer el archivo') });
    } finally {
      setRevisando(false);
    }
  };

  const elegirArchivo = (e) => {
    const elegido = e.target.files?.[0];
    if (!elegido) return;
    setArchivo(elegido);
    revisar(elegido);
  };

  const cantidad = vista?.documentos.length ?? 0;

  const enviar = async () => {
    const confirmacion = await Swal.fire({
      icon: 'question',
      title: 'Enviar la simulación',
      text: `Se reemiten ${cantidad} documentos en certificación, con fecha de hoy y folios de certificación, y se envían juntos al SII.`,
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmacion.isConfirmed) return;
    setEnviando(true);
    try {
      const resultado = await conBloqueo('Enviando la simulación al SII...', () => enviarSimulacionFacturador(empresa.id_company, archivo));
      onEnviado();
      // La revisión de nuevo (folios que quedan) se bloquea con su propio aviso: va después de ver el resultado
      await Swal.fire({
        icon: resultado.trackId ? 'success' : 'warning',
        title: resultado.trackId ? 'Simulación enviada' : 'El SII no recibió el envío',
        text: resultado.trackId ? `TrackID ${resultado.trackId}. Consulta su estado en "Envíos al SII".` : 'Quedó por enviar: reintenta desde "Envíos al SII".',
      });
      revisar(archivo);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se envió', text: mensajeError(err, 'No se pudo enviar la simulación') });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <section className="fe-panel">
        <h3 className="fe-panel-title">Simulación</h3>
        <ol className="fe-hint" style={{ paddingLeft: 18, margin: '0 0 12px' }}>
          <li>
            En el SII: Servicios online → Factura electrónica → "Respaldo de DTE y/o IECV" → "Descargar información electrónica",
            DTE emitidos de los últimos 2 meses (de 10 a 100 documentos).
          </li>
          <li>Súbelo aquí: cada documento se reemite en certificación con fecha de hoy, folios de certificación y los mismos clientes, servicios y montos.</li>
          <li>Cuando el SII lo acepte, declara su TrackID en "Declarar avance".</li>
        </ol>
        <div className="fe-form-group" style={{ maxWidth: 520 }}>
          <label>Documentos emitidos (.xml)</label>
          <input type="file" accept=".xml" onChange={elegirArchivo} disabled={revisando || enviando} />
        </div>
        {revisando && <p className="fe-loading">Revisando los documentos...</p>}
      </section>

      {vista && !revisando && (
        <section className="fe-panel">
          <div className="fe-cert-cabecera">
            <h3 className="fe-panel-title">{cantidad} documentos para reemitir</h3>
            {vista.problemas.length === 0 && <span className="fe-badge fe-badge--verde">Listos para enviar</span>}
          </div>
          {vista.problemas.length > 0 && (
            <div className="fe-aviso fe-aviso--rojo">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {vista.problemas.map((problema) => <li key={problema}>{problema}</li>)}
              </ul>
            </div>
          )}
          <div className="fe-table-container">
            <table className="fe-table">
              <thead>
                <tr><th>Original</th><th>Fecha</th><th>Cliente</th><th>Servicio</th><th className="fe-num">Total</th></tr>
              </thead>
              <tbody>
                {vista.documentos.map((d) => (
                  <tr key={`${d.tipo}-${d.folioOriginal}`} className={d.error ? 'fe-cert-fila-error' : ''}>
                    <td>{NOMBRES_TIPO[d.tipo] || `Documento ${d.tipo}`} N° {d.folioOriginal}</td>
                    <td>{fechaCorta(d.fechaOriginal)}</td>
                    <td>{d.receptor}<div className="fe-muted">{d.rutReceptor}</div></td>
                    <td>
                      {d.glosa}
                      {d.error && <div className="fe-cert-error">{d.error}</div>}
                    </td>
                    <td className="fe-num">{pesos(d.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fe-form-actions fe-cert-acciones">
            <button className="fe-btn fe-btn--primary" disabled={enviando || vista.problemas.length > 0} onClick={enviar}>
              {enviando ? 'Enviando...' : `Generar y enviar al SII (${cantidad} documentos)`}
            </button>
          </div>
        </section>
      )}
    </>
  );
}

// ─── Libros enviados ─────────────────────────────────────────────────────────

/** Solo LOK (libro cuadrado) es aceptado; otro código va tal cual, con la glosa del SII. */
function EstadoLibro({ libro }) {
  if (libro.aceptado) return <><span className="fe-badge fe-badge--verde">Aceptado</span> {libro.estado} · {libro.glosa}</>;
  if (libro.estado) return <><span className="fe-badge fe-badge--amarillo">{libro.estado}</span> {libro.glosa}</>;
  if (libro.trackId) return <span className="fe-badge fe-badge--azul">Enviado al SII</span>;
  return <><span className="fe-badge fe-badge--rojo">No llegó al SII</span> {libro.glosa} Vuelve a enviarlo desde el set.</>;
}

function LibrosEnviados({ empresa, libros, onCambio }) {
  const [trabajando, setTrabajando] = useState(null);

  const consultar = async (libro) => {
    setTrabajando(libro.id);
    try {
      const r = await conBloqueo('Consultando el estado del libro en el SII...', () => consultarEstadoLibroFacturador(empresa.id_company, libro.id));
      Swal.fire({ icon: r.aceptado ? 'success' : 'info', title: `Estado del libro: ${r.estado}`, text: r.glosa ?? '' });
      onCambio();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo consultar el estado') });
    } finally {
      setTrabajando(null);
    }
  };

  return libros.map((libro) => (
    <section key={libro.id} className="fe-panel">
      <div className="fe-cert-cabecera">
        <h3 className="fe-panel-title">{NOMBRES_LIBRO[libro.clase] ?? libro.clase} · N° de atención {libro.numeroAtencion} · período {libro.periodo}</h3>
        <span className="fe-muted">{libro.trackId ? `TrackID ${libro.trackId}` : 'Sin TrackID'}</span>
      </div>
      <p className="fe-cert-estado-libro"><EstadoLibro libro={libro} /></p>
      {libro.trackId && (
        <div className="fe-form-actions fe-cert-acciones">
          <button className="fe-btn fe-btn--secondary" disabled={trabajando === libro.id} onClick={() => consultar(libro)}>Consultar estado en el SII</button>
        </div>
      )}
    </section>
  ));
}

// ─── Intercambio de información ──────────────────────────────────────────────

function Intercambio({ empresa }) {
  const [archivo, setArchivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [rechazos, setRechazos] = useState({}); // clave del documento → motivo
  const [generadoCon, setGeneradoCon] = useState('{}');
  const [generando, setGenerando] = useState(false);

  const generar = async (envio, conRechazos) => {
    setGenerando(true);
    try {
      setResultado(await conBloqueo('Generando las respuestas...', () => responderIntercambioFacturador(empresa.id_company, envio, conRechazos)));
      setGeneradoCon(JSON.stringify(conRechazos));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron generar las respuestas') });
    } finally {
      setGenerando(false);
    }
  };

  const elegirArchivo = (e) => {
    const envio = e.target.files?.[0];
    if (!envio) return;
    setArchivo(envio);
    setRechazos({});
    setResultado(null);
    generar(envio, {});
  };

  const alternarRechazo = (clave) =>
    setRechazos((actuales) => {
      const nuevos = { ...actuales };
      if (clave in nuevos) delete nuevos[clave];
      else nuevos[clave] = '';
      return nuevos;
    });

  const desactualizado = JSON.stringify(rechazos) !== generadoCon;
  const faltaMotivo = Object.values(rechazos).some((motivo) => !motivo.trim());

  return (
    <>
      <ol className="fe-hint" style={{ paddingLeft: 18, margin: '0 0 12px' }}>
        <li>En el ambiente de certificación del SII, opción "Intercambio de información", descarga el envío que te mandó el SII.</li>
        <li>Súbelo aquí: el facturador arma el acuse de recibo, el resultado de cada documento y el recibo de mercaderías, firmados con el certificado de la empresa.</li>
        <li>Descarga las respuestas y súbelas en esa misma opción del SII.</li>
      </ol>
      <div className="fe-form-group">
        <label>Envío recibido del SII (.xml)</label>
        <input type="file" accept=".xml" onChange={elegirArchivo} disabled={generando} />
      </div>
      {generando && <p className="fe-loading">Generando respuestas...</p>}

      {resultado && !generando && (
        <>
          {!resultado.envio.recibido && (
            <div className="fe-aviso fe-aviso--amarillo" style={{ marginTop: 12 }}>
              El envío está dirigido al RUT {resultado.envio.rutReceptor}, no a esta empresa: el acuse lo rechaza.
            </div>
          )}
          <div className="fe-table-container" style={{ marginTop: 12 }}>
          <table className="fe-table">
            <thead>
              <tr><th>Documento</th><th>Fecha</th><th>Emisor</th><th className="fe-num">Total</th><th>Respuesta</th></tr>
            </thead>
            <tbody>
              {resultado.documentos.map((d) => (
                <tr key={d.clave}>
                  <td>{NOMBRES_TIPO[d.tipo] || `Documento ${d.tipo}`} N° {d.folio}</td>
                  <td>{fechaCorta(d.fechaEmision)}</td>
                  <td>{d.rutEmisor}</td>
                  <td className="fe-num">{pesos(d.montoTotal)}</td>
                  <td>
                    {!d.recibido ? (
                      <span className="fe-muted">Rechazado: no es para esta empresa</span>
                    ) : (
                      <>
                        <label className="fe-checkbox">
                          <input type="checkbox" checked={d.clave in rechazos} onChange={() => alternarRechazo(d.clave)} /> Rechazar
                        </label>
                        {d.clave in rechazos && (
                          <input
                            type="text"
                            placeholder="Motivo del rechazo"
                            value={rechazos[d.clave]}
                            maxLength={240}
                            onChange={(e) => setRechazos((r) => ({ ...r, [d.clave]: e.target.value }))}
                            style={{ marginTop: 6 }}
                          />
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="fe-form-actions">
            {desactualizado ? (
              <button className="fe-btn fe-btn--primary" disabled={faltaMotivo} onClick={() => generar(archivo, rechazos)}>
                Volver a generar las respuestas
              </button>
            ) : (
              resultado.archivos.map((a) => (
                <button key={a.nombre} className="fe-btn fe-btn--success" onClick={() => descargar(a)}>{a.nombre}</button>
              ))
            )}
          </div>
          {desactualizado && <p className="fe-hint">Cambiaste las respuestas: vuelve a generarlas antes de descargar.</p>}
        </>
      )}
    </>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

const PESTANAS = [
  ['set', 'Set de pruebas'],
  ['simulacion', 'Simulación'],
  ['envios', 'Envíos al SII'],
  ['intercambio', 'Intercambio'],
];

/**
 * Certificación de una empresa ante el SII (/admin/facturador/:idCompany/certificacion):
 * el set de pruebas (se arma desde el archivo del SII y se envía en un solo
 * sobre) con sus libros de ventas y compras, la simulación con documentos
 * reales, los envíos con su estado y sus muestras impresas, y el intercambio
 * de información. Las pestañas solo se
 * ocultan: el archivo cargado y lo escrito se mantienen al cambiar de una a otra.
 */
export default function CertificacionPage() {
  const { idCompany } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(undefined);
  const [pestana, setPestana] = useState('set');
  const [texto, setTexto] = useState(null);
  const [sets, setSets] = useState(null);
  const [guardados, setGuardados] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [libros, setLibros] = useState([]);

  useEffect(() => {
    getFacturadorEmpresas()
      .then((datos) => setEmpresa(datos.empresas.find((e) => String(e.id_company) === String(idCompany)) ?? null))
      .catch((err) => {
        setEmpresa(null);
        Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo cargar la empresa') });
      });
  }, [idCompany]);

  const cargarEnvios = useCallback(() => {
    Promise.all([getEnviosCertificacion(idCompany), getLibrosCertificacion(idCompany)])
      .then(([lotes, enviados]) => {
        setEnvios(lotes);
        setLibros(enviados);
      })
      .catch((err) => Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron cargar los envíos') }));
  }, [idCompany]);

  const activado = Boolean(empresa?.activado);
  useEffect(() => {
    if (!activado) return;
    cargarEnvios();
    getReceptoresFacturador(idCompany).then(setGuardados).catch(() => setGuardados([]));
  }, [activado, cargarEnvios, idCompany]);

  // Un envío nuevo o un estado consultado cambian de qué set básico salen los libros
  const actualizar = () => {
    cargarEnvios();
    if (texto) vistaPreviaSetFacturador(idCompany, texto).then(setSets).catch(() => {});
  };

  const elegirSet = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    // El SII entrega el set en ISO-8859-1
    const contenido = new TextDecoder('iso-8859-1').decode(await archivo.arrayBuffer());
    try {
      setSets(await conBloqueo('Leyendo el set de pruebas...', () => vistaPreviaSetFacturador(idCompany, contenido)));
      setTexto(contenido);
    } catch (err) {
      setSets(null);
      setTexto(null);
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo leer el set de pruebas') });
    }
  };

  const otros = (sets ?? []).filter((s) => !s.sePuedeEnviar);
  const cantidadEnvios = envios.length + libros.length;

  return (
    <div className="admin-facturador-page">
      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/admin/facturador')}>← Volver</button>
            <div>
              <h1>Certificación{empresa ? ` · ${empresa.empresa}` : ''}</h1>
              {empresa && (
                <p className="afe-subtitulo">
                  RUT {empresa.rut}
                  {empresa.activado && ` · Ambiente de ${empresa.ambiente === 'produccion' ? 'producción' : 'certificación'}`}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="fe-page">
        <div className="fe-cert">
          {empresa === undefined && <p className="fe-loading">Cargando empresa...</p>}
          {empresa === null && <div className="fe-aviso fe-aviso--rojo">No se encontró la empresa {idCompany}.</div>}
          {empresa && !empresa.activado && (
            <div className="fe-aviso fe-aviso--amarillo">Esta empresa no tiene el facturador activado: actívalo desde el facturador.</div>
          )}

          {activado && (
            <>
              <div className="fe-opciones">
                {PESTANAS.map(([clave, nombre]) => (
                  <button key={clave} className={`fe-tipo-btn ${pestana === clave ? 'fe-tipo-btn--activo' : ''}`} onClick={() => setPestana(clave)}>
                    {nombre}{clave === 'envios' && cantidadEnvios > 0 ? ` (${cantidadEnvios})` : ''}
                  </button>
                ))}
              </div>

              <div hidden={pestana !== 'set'}>
                <section className="fe-panel">
                  <h3 className="fe-panel-title">Archivo del set de pruebas</h3>
                  <p className="fe-hint" style={{ margin: '0 0 12px' }}>
                    Sube el archivo que entrega el SII en "Generación de set de pruebas". Cada documento original va a un receptor distinto
                    (cliente real) y las notas van al receptor del documento que corrigen. Antes, carga los folios de certificación en Configuración.
                  </p>
                  <div className="fe-form-group" style={{ maxWidth: 520 }}>
                    <label>Archivo del set de pruebas (.txt)</label>
                    <input type="file" accept=".txt" onChange={elegirSet} />
                  </div>
                  {otros.length > 0 && (
                    <p className="fe-hint" style={{ marginTop: 10 }}>
                      También trae: {otros.map((s) => `${s.nombre.toLowerCase()} (N° ${s.numeroAtencion})`).join(', ')}. Esos se preparan aparte.
                    </p>
                  )}
                </section>
                {sets && texto && sets.filter((s) => s.sePuedeEnviar).map((set) =>
                  set.libro ? (
                    <LibroDelSet key={set.numeroAtencion} empresa={empresa} texto={texto} set={set} onEnviado={actualizar} />
                  ) : (
                    <SetDeDocumentos key={set.numeroAtencion} empresa={empresa} texto={texto} set={set} guardados={guardados} onEnviado={actualizar} />
                  )
                )}
              </div>

              <div hidden={pestana !== 'simulacion'}>
                <Simulacion empresa={empresa} onEnviado={actualizar} />
              </div>

              <div hidden={pestana !== 'envios'}>
                <h2 className="fe-cert-seccion">Documentos</h2>
                <EnviosCertificacion empresa={empresa} envios={envios} onCambio={actualizar} />
                {libros.length > 0 && (
                  <>
                    <h2 className="fe-cert-seccion">Libros</h2>
                    <LibrosEnviados empresa={empresa} libros={libros} onCambio={actualizar} />
                  </>
                )}
              </div>

              <div hidden={pestana !== 'intercambio'}>
                <section className="fe-panel">
                  <h3 className="fe-panel-title">Intercambio de información</h3>
                  <Intercambio empresa={empresa} />
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
